import {
  CRMConversationAutoReplyStatus,
  CRMConversationAutoResponseStatus,
} from "../../types";

export enum EmailMessageType {
  text = "text",
}

export type EmailAttachment = {
  attachment_id: string;
  filename: string;
  media_id: string;
  mime_type: string;
  size: number;
};

export type EmailIncomingMessagePayloadDto = {
  attachments?: EmailAttachment[];
  body: string;
  from: string;
  message_id: string;
  subject: string;
  thread_id: string;
  timestamp: number;
  to: string;
};

export type EmailOutgoingMessagePayloadDto = {
  // legacy shape
  message?: {
    text: string;
  };
  // new shape: body in HTML format
  text?: string;
  attachments?: EmailAttachment[];
  message_id: string;
  name: string;
};

type EmailMessagePayload = {
  timestamp: string;
  type: EmailMessageType;
  text?: string;
  subject?: string;
  from?: string;
  to?: string;
  threadId?: string;
  messageId?: string;
  attachments?: EmailAttachment[];
};

export type EmailMessageModel = {
  id: string;
  conv_id: string;
  name: string;
  type: "outgoing" | "incoming" | "info";
  msg_type: EmailMessageType;
  from: string;
  msg_id: string;
  payload: EmailMessagePayload;
  entity_id: string;
  read: boolean;
  status: string | null;
  attended_by?: string;
  auto_response: CRMConversationAutoResponseStatus;
  auto_reply: CRMConversationAutoReplyStatus | null;
};

// DTO type from API (before transformation)
export type EmailMessageDto = {
  id: string;
  conv_id: string;
  type: "outgoing" | "incoming" | "info";
  msg_type: EmailMessageType;
  inserted_at: string;
  from: string;
  msg_id: string;
  payload: EmailIncomingMessagePayloadDto | EmailOutgoingMessagePayloadDto;
  entity_id: string;
  read: boolean;
  status: string | null;
  auto_response: number;
  attended_by: string;
  auto_reply: CRMConversationAutoReplyStatus | null;
};

export const transformEmailMessageDtoToModel = (
  dto: EmailMessageDto
): EmailMessageModel => {
  const { payload } = dto;

  const transformedPayload: EmailMessagePayload =
    payload && "body" in payload
      ? {
          text: payload.body,
          timestamp: dto.inserted_at,
          type: dto.msg_type,
          subject: payload.subject,
          from: payload.from,
          to: payload.to,
          threadId: payload.thread_id,
          messageId: payload.message_id,
          attachments: payload.attachments,
        }
      : {
          text: payload?.text ?? payload?.message?.text ?? "",
          timestamp: dto.inserted_at,
          type: dto.msg_type,
          messageId: payload?.message_id,
          attachments: payload?.attachments,
        };

  return {
    ...dto,
    name: dto.conv_id,
    payload: transformedPayload,
  };
};

// conv_id has the shape "<senderEmail>_<threadId>", e.g.
// "4nirmall@gmail.com_19e96673baf7bf22"
export const parseEmailConvId = (
  convId: string
): { email: string; threadId: string } => {
  const separatorIndex = convId.lastIndexOf("_");

  if (separatorIndex === -1) {
    return { email: convId, threadId: convId };
  }

  return {
    email: convId.slice(0, separatorIndex),
    threadId: convId.slice(separatorIndex + 1),
  };
};
